import { IStreamSubscription } from './EventStreamSubscription';
import { Matrix3 } from './Matrix3';
import { RenderDevice } from './RenderDevice';
import { SceneObjectType } from './SceneObjectType';
import { SpriteBatch } from './SpriteBatch';
import { ColorEffect, GlowEffect, ShadowEffect } from './TextRenderParameters';
import { TextSceneObject } from './TextSceneObject';
import { TextSprite } from './TextSprite';
import { TtfFontResource } from './TtfFontResource';
import { TtfFontUtils } from './TtfFontUtils';
import { TtfRenderComponent } from './TtfRenderComponent';
import { Vector2 } from './Vector2';

export class TtfTextSceneObject extends TextSceneObject {
  get runtimeType(): SceneObjectType {
    return SceneObjectType.Text;
  }
  private _sprite: TextSprite | null = null;

  private readonly _renderDevice: RenderDevice;
  private readonly _fontResource: TtfFontResource;

  public originalTextBounds: Vector2 | null = null;
  public forceFontSize: number | null = null;

  private _deviceLost: IStreamSubscription | null = null;

  private glowEffect: GlowEffect | null = null;
  private shadowEffect: ShadowEffect | null = null;
  private colorEffect: ColorEffect | null = null;
  private renderComponent: TtfRenderComponent | null = null;

  constructor(renderDevice: RenderDevice, fontResource: TtfFontResource) {
    super();
    this._renderDevice = renderDevice;
    this._fontResource = fontResource;
    this._sprite = null;
    this._deviceLost = null;
  }

  public drawImpl(spriteBatch: SpriteBatch, transform: Matrix3): void {
    this.build();
    spriteBatch.drawRect(this._sprite!.texture, this._sprite!.rect, this._sprite!.logicalRect, transform);
  }

  public addGlowEffect(effect: GlowEffect): void {
    this.glowEffect = effect;
    this.invalidate(); // Force rebuild with new effect
  }

  public addShadowEffect(effect: ShadowEffect): void {
    this.shadowEffect = effect;
    this.invalidate(); // Force rebuild with new effect
  }

  public addColorEffect(effect: ColorEffect): void {
    this.colorEffect = effect;
    this.invalidate(); // Force rebuild with new effect
  }

  public setRenderComponent(component: TtfRenderComponent | null): void {
    this.renderComponent = component;
    this.invalidate();
  }

  public getRenderComponent(): TtfRenderComponent | null {
    return this.renderComponent;
  }

  /**
   * Enhanced method to add multiple effects at once
   */
  public addEffects(
    glowEffect?: GlowEffect,
    shadowEffect?: ShadowEffect,
    colorEffect?: ColorEffect
  ): void {
    let needsRebuild = false;

    if (glowEffect) {
      this.glowEffect = glowEffect;
      needsRebuild = true;
    }

    if (shadowEffect) {
      this.shadowEffect = shadowEffect;
      needsRebuild = true;
    }

    if (colorEffect) {
      this.colorEffect = colorEffect;
      needsRebuild = true;
    }

    if (needsRebuild) {
      this.invalidate();
    }
  }

  /**
   * Clears all effects and rebuilds
   */
  public clearEffects(): void {
    this.glowEffect = null;
    this.shadowEffect = null;
    this.colorEffect = null;
    this.invalidate();
  }

  public build(): void {
    if (!this._sprite) {
      this._build();
    }
    if (!this.originalTextBounds) {
      this.originalTextBounds = this._sprite!.logicalRect.size.clone();
    }
  }

  private _build(): void {
    const fontFamily: string = TtfFontUtils.getFontFamily(this._fontResource.id);

    if (this.renderComponent) {
      this._sprite = this.renderComponent.build(
        this._renderDevice,
        this.textFormated,
        this.size,
        fontFamily,
        this.fontSize,
        this.lineSpacing,
        this.halign,
        this.valign,
        this.isMultiLineText,
        this.textRenderParams.lang,
        this.textRenderParams.letterSpacing
      );
    } else {
      // Apply effects to text render parameters (legacy path)
      this.textRenderParams.glowEffect = this.glowEffect;
      this.textRenderParams.shadowEffect = this.shadowEffect;
      this.textRenderParams.colorEffect = this.colorEffect;

      this._sprite = this._fontResource.data!.build(
        this.textRenderParams,
        this.textFormated,
        this.size,
        fontFamily,
        this.fontSize,
        this.lineSpacing,
        this.halign,
        this.valign,
        this.isMultiLineText
      );
    }
  }

  public getLines(): string[] | null {
    return this._fontResource && this._fontResource.data
      ? this._fontResource.data.getLines()
      : null;
  }

  public get maximumTextWidth(): number | null {
    return this._fontResource && this._fontResource.data
      ? this._fontResource.data.maximumTextWidth
      : null;
  }

  public calculateTextWidth(text: string): number | null {
    return this._fontResource && this._fontResource.data
      ? this._fontResource.data.measureText(text)
      : null;
  }

  public invalidate(): void {
    this._free();
  }

  public initializeImpl(): void {
    this._deviceLost = this._renderDevice.lost.listen(() => this._onDeviceLost());
  }

  public deinitializeImpl(): void {
    this._free();
    this._deviceLost?.cancel();
  }

  public getTextBounds(): Vector2 {
    this.build();
    return this._sprite!.logicalRect.size.clone();
  }

  private _free(): void {
    if (this._sprite) {
      this._fontResource.data!.dispose(this._sprite);
      this._sprite = null;
    }
  }

  private _onDeviceLost(): void {
    this._free();
  }
}
